import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  getUser,
  getAllUsers,
  reviewUserById,
  updateUserById,
} from "../../../Services/UserService";
import * as siteService from "../../../Services/siteService";
import {
  ROLE_FIELDS,
  COMPANY_FIELDS,
  APPROVAL_FIELDS,
  EMPLOYMENT_STATUS_FIELDS,
  MESSAGES,
  VALIDATION_MESSAGES,
} from "../constant";
import {
  BLOOD_GROUP_OPTIONS,
  YES_NO_OPTIONS,
  GENDER_OPTIONS,
} from "./constants";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import * as route from "../../../routes";
import { Loader } from "../../../Components/Common";
import getInitials from "../../../Utils/getInitials";
import { FaCamera } from "react-icons/fa";
import EducationRepeater from "./EducationRepeater";
import { formatDateDDMMYYYY } from "../../../Utils";
import { GET_ALL_PAGINATION } from "../../../Constants/Strings";

const UserInfoTab = ({
  isUserEditable,
  setIsUserEditable,
  isHRAdminEditable,
  setIsHRAdminEditable,
  setIsUserOnboarded,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();

  const isProfilePage = location.pathname.startsWith("/profile");
  const isViewMode = location.pathname.includes("view");

  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [assignedSites, setAssignedSites] = useState([]);

  const [initialValues, setInitialValues] = useState({
    profile_photo: "",
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    gender: "",
    role: "",
    is_approved: "false",
    department: "",
    designation: "",
    manager: "",
    manager_name: "",
    joining_date: "",
    employment_status: "PENDING_ONBOARDING",
    date_of_exit: "",
    company: "",
    employee_code: "",
    work_location: "",
    shift: "",
    pan_number: "",
    aadhar_number: "",
    esic_account_number: "",
    mark_attendance_without_site: false,
    default_site: "",
    default_site_name: "",
    current_address: "",
    permanent_address: "",
    birth_date: "",
    emergency_contact_number: "",
    relationship_contact: "",
    blood_group: "",
    height: "",
    weight: "",
    caste_subcaste: "",
    education: [],
    form_given_on: "",
    acc_opened_on: "",
    prev_emp_pf_number: "",
    account_opening_letter: "",
    qualification: "",
    total_experience: "",
    is_fresher: "",
    uan_number: "",
    salary_account_number: "",
    bank_ifsc_code: "",
    bank_name: "",
  });

  const previewUrl = useMemo(() => {
    if (photoFile) return URL.createObjectURL(photoFile);
    return initialValues.profile_photo || "";
  }, [photoFile, initialValues.profile_photo]);

  const formatRole = (role) => {
    const found = ROLE_FIELDS.find((r) => r.value === role);
    return found ? (
      found.label
    ) : (
      <span className="italic text-gray-400">Not Provided</span>
    );
  };

  const displayValue = (value) =>
    value && value !== "" ? (
      value
    ) : (
      <span className="italic text-gray-400">Not Provided</span>
    );

  const fetchUser = async () => {
    try {
      setLoadingUser(true);

      let u = null;

      if (isProfilePage) {
        const res = await getUser();
        if (res.status === 200) {
          u = res.data;
        } else {
          toast.error(MESSAGES.NO_USER_DATA);
          return;
        }
      } else {
        const res = await getAllUsers({ id: userId });
        if (res.status === 200 && res.data.results.length > 0) {
          u = res.data.results[0];
        } else {
          toast.error(MESSAGES.NO_USER_DATA);
          return;
        }
      }
      // Locked-by-default once past the initial pending state (onboarded or
      // off-boarded); only pending-onboarding users get open-by-default fields.
      const isSettled = (u.employment_status || "PENDING_ONBOARDING") !== "PENDING_ONBOARDING";
      setIsOnboarded(isSettled);
      setIsUserOnboarded(isSettled);
      setInitialValues({
        profile_photo: u.profile_photo || "",
        first_name: u.first_name || "",
        last_name: u.last_name || "",
        email: u.email || "",
        mobile: u.mobile || "",
        gender: u.gender || "",
        role: u.role || "",
        is_approved: u.is_approved ? "true" : "false",
        department: u.department || "",
        designation: u.designation || "",
        manager: u.manager || "",
        manager_name: u.manager_name || "",
        joining_date: u.date_of_joining || null,
        employment_status: u.employment_status || "PENDING_ONBOARDING",
        date_of_exit: u.date_of_exit || null,
        company: u.company || "",
        employee_code: u.employee_code || "",
        work_location: u.work_location || "",
        shift: u.shift || "",
        pan_number: u.pan_number || "",
        aadhar_number: u.aadhar_number || "",
        esic_account_number: u.esic_account_number || "",
        mark_attendance_without_site: u.mark_attendance_without_site ?? "false",
        default_site: u.default_site || "",
        default_site_name: u.default_site_name || "",
        current_address: u.current_address || "",
        permanent_address: u.permanent_address || "",
        birth_date: u.birth_date || null,
        emergency_contact_number: u.emergency_contact_number || "",
        relationship_contact: u.relationship_contact || "",
        blood_group: u.blood_group || "",
        height: u.height || "",
        weight: u.weight || "",
        caste_subcaste: u.caste_subcaste || "",
        education:
          u.education && u.education.length > 0
            ? u.education
            : [{ degree: "", university: "", percentage: "" }],
        qualification: u.qualification || "",
        total_experience:
          u.total_experience !== null ? u.total_experience.split(".")[0] : null,
        is_fresher: u.is_fresher ?? false,
        acc_opened_on: u.acc_opened_on || null,
        prev_emp_pf_number: u.prev_emp_pf_number || "",
        account_opening_letter: u.acc_opening_letter || "",
        uan_number: u.uan_number || "",
        salary_account_number: u.salary_account_number || "",
        bank_ifsc_code: u.bank_ifsc_code || "",
        bank_name: u.bank_name || "",
      });
    } catch (err) {
      toast.error(extractErrorMessage(err) || MESSAGES.USER_DATA_ERROR);
    } finally {
      setLoadingUser(false);
      setIsUserEditable(false);
      setIsHRAdminEditable(false);
    }
  };

  const fetchAllUsersList = async () => {
    try {
      const res = await getAllUsers({ paginate: "false" });
      if (res.status === 200) {
        setAllUsers(res.data.results || res.data || []);
      }
    } catch (err) {
      toast.error(MESSAGES.USERS_LOAD_FAILED);
    }
  };

  const fetchAssignedSites = async () => {
    try {
      const res = await siteService.getAssignedSites(GET_ALL_PAGINATION);
      if (res.status === 200) {
        setAssignedSites(res.data.results || []);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || MESSAGES.SITES_LOAD_FAILED);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [userId, isProfilePage]);

  useEffect(() => {
    fetchAllUsersList();
  }, []);

  useEffect(() => {
    if (isProfilePage && !isViewMode) {
      fetchAssignedSites();
    }
  }, [isProfilePage, isViewMode]);

  const reviewValidation = Yup.object({
    role: Yup.string().required(VALIDATION_MESSAGES.ROLE_REQUIRED),
    is_approved: Yup.string().required(
      VALIDATION_MESSAGES.APPROVAL_STATUS_REQUIRED,
    ),
    joining_date: Yup.string().required(VALIDATION_MESSAGES.joining_date),
    designation: Yup.string().required(VALIDATION_MESSAGES.designation),
  });

  const profileValidation = Yup.object({
    first_name: Yup.string().required(
      VALIDATION_MESSAGES.FIRST_NAME_REQUIRED || "First name required",
    ),
    last_name: Yup.string().required(
      VALIDATION_MESSAGES.LAST_NAME_REQUIRED || "Last name required",
    ),
    gender: Yup.string().required(
      VALIDATION_MESSAGES.GENDER_REQUIRED || "Gender required",
    ),
    email: Yup.string()
      .email(VALIDATION_MESSAGES.INVALID_MAIL || "Invalid email")
      .required(VALIDATION_MESSAGES.MAIL_REQUIRED || "Email required"),
    salary_account_number: Yup.string().required(
      VALIDATION_MESSAGES.SALARY_ACC_NO_REQUIRED ||
        "Account number is required",
    ),
    bank_ifsc_code: Yup.string().required(
      VALIDATION_MESSAGES.IFSC_CODE_REQUIRED || "Bank IFSC code is required",
    ),
    bank_name: Yup.string().required(VALIDATION_MESSAGES.bank_name),
    uan_number: Yup.string().required(
      VALIDATION_MESSAGES.uan_number || "UAN NUmber is required",
    ),
    pan_number: Yup.string().matches(
      /^[A-Z]{5}[0-9]{4}[A-Z]$/,
      { message: VALIDATION_MESSAGES.pan_number, excludeEmptyString: true },
    ),
    aadhar_number: Yup.string().matches(
      /^[0-9]{12}$/,
      { message: VALIDATION_MESSAGES.aadhar_number, excludeEmptyString: true },
    ),
    birth_date: Yup.string().required(
      VALIDATION_MESSAGES.birth_date || "Birth Date is required",
    ),
    current_address: Yup.string().required(
      VALIDATION_MESSAGES.current_address || "Current Address is required",
    ),
    permanent_address: Yup.string().required(
      VALIDATION_MESSAGES.permanent_address || "Permanent Address is required",
    ),
    emergency_contact_number: Yup.string()
      .required(
        VALIDATION_MESSAGES.emergency_contact_number ||
          "Emergency Contact is required",
      )
      .matches(
        /^[0-9]{10}$/,
        VALIDATION_MESSAGES.emergency_contact_number_digits ||
          "Emergency contact number must be exactly 10 digits",
      ),
    education: Yup.array()
      .of(
        Yup.object().shape({
          degree: Yup.string().required("Degree is required"),
          university: Yup.string().required("University is required"),
          percentage: Yup.number()
            .typeError("Percentage must be a number")
            .required("Percentage is required"),
        }),
      )
      .min(1, "At least one education record is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    if (isViewMode) return;

    setSubmitting(true);
    setLoading(true);

    try {
      if (isProfilePage) {
        const form = new FormData();

        Object.keys(values).forEach((key) => {
          if (key === "default_site_name") {
            return;
          } else if (key === "education") {
            form.append("education", JSON.stringify(values.education || []));
          } else if (key === "profile_photo") {
            if (photoFile) {
              form.append("profile_photo", photoFile); // only when new file chosen
            }
          } else if (key === "account_opening_letter") {
            if (values.account_opening_letter instanceof File) {
              form.append("acc_opening_letter", values.account_opening_letter);
            }
          } else {
            form.append(key, values[key] ?? "");
          }
        });

        form.append("id", userId);

        if (photoFile) {
          form.append("profile_photo", photoFile);
        }

        const res = await updateUserById(form);

        if (res.status === 200 || res.status === 204) {
          toast.success(MESSAGES.PROFILE_UPDATE_SUCCESS);
          setInitialValues((prev) => ({
            ...prev,
            account_opening_letter: res.data.acc_opening_letter, // The updated URL
          }));
          if (!isViewMode) navigate(route.PROFILE);
        } else {
          toast.error(
            extractErrorMessage(res) || MESSAGES.PROFILE_UPDATE_FAILED,
          );
        }
      } else {
        const payload = {
          id: userId,
          role: values.role,
          is_approved:
            values.is_approved === "true" || values.is_approved === true,
          department: values.department || null,
          designation: values.designation || null,
          manager: values.manager || null,
          date_of_joining: values.joining_date || null,
          employment_status: values.employment_status,
          date_of_exit: values.date_of_exit || null,
          mark_attendance_without_site: values.mark_attendance_without_site,
          company: values.company || null,
          employee_code: values.employee_code || null,
          work_location: values.work_location || null,
          shift: values.shift || null,
        };

        const res = await reviewUserById(payload);

        if (res.status === 200 || res.status === 204) {
          toast.success(MESSAGES.USER_UPDATE_SUCCESS);
          navigate(route.USER_PAGE);
        } else {
          toast.error(extractErrorMessage(res) || MESSAGES.USER_UPDATE_FAILED);
        }
      }
      fetchUser();
    } catch (err) {
      toast.error(MESSAGES.UPDATE_ERROR);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  if (loadingUser)
    return (
      <div className="flex justify-center pt-10">
        <Loader size={6} />
      </div>
    );

  const inputClass =
    "mt-1 w-[95%] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary";

  return (
    <div className="p-4">
      {/* Header with avatar + name */}
      <div className="mb-3">
        <div className="flex items-center gap-4">
          <div className="relative">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Profile"
                className="object-cover rounded-full w-20 h-20 border-4 border-white shadow"
              />
            ) : (
              <div
                className="flex items-center justify-center rounded-full w-20 h-20 border-4 border-white shadow text-xl font-semibold text-white"
                style={{ background: "#64748b" }}
              >
                {getInitials(
                  `${initialValues.first_name} ${initialValues.last_name}`,
                )}
              </div>
            )}

            {isProfilePage && !isViewMode && (
              <label className="absolute bottom-0 right-0 bg-primary p-2 rounded-full cursor-pointer shadow hover:bg-secondary transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.currentTarget.files?.[0];
                    if (f) {
                      setPhotoFile(f);
                    }
                  }}
                />
                <FaCamera className="text-white text-sm" />
              </label>
            )}
          </div>

          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              {`${initialValues.first_name} ${initialValues.last_name}`.trim() ||
                "User"}

              {initialValues.employment_status === "ONBOARDED" ? (
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 border border-green-300">
                  Onboarded
                </span>
              ) : initialValues.employment_status === "OFFBOARDED" ? (
                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 border border-red-300">
                  Off-boarded
                </span>
              ) : (
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 border border-yellow-300">
                  Pending Onboarding
                </span>
              )}
            </h2>

            <p className="text-sm text-gray-500">
              {formatRole(initialValues.role)}
            </p>
          </div>
        </div>

        <hr className="mt-4 mb-2 border-gray-200" />
      </div>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={isProfilePage ? profileValidation : reviewValidation}
      >
        {({ values, setFieldValue }) => (
          <Form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* LEFT COLUMN (personal, education, emergency) */}
              <div className="space-y-6">
                {/* Personal Details */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Personal Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* First name */}
                    <div>
                      <label className="text-sm text-gray-700">
                        First Name <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.first_name)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="first_name"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter first name"
                          />
                          <ErrorMessage
                            name="first_name"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    {/* Last name */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Last Name <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.last_name)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="last_name"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter last name"
                          />
                          <ErrorMessage
                            name="last_name"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    {/* Gender */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Gender <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.gender)}
                        </p>
                      ) : (
                        <>
                          <Field
                            as="select"
                            name="gender"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          >
                            {GENDER_OPTIONS.map((g) => (
                              <option key={g.value} value={g.value}>
                                {g.label}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="gender"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Email <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.email)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="email"
                            type="email"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter email address"
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    {/* Mobile */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Mobile Number
                      </label>
                      <p className="mt-1 text-sm">
                        {displayValue(initialValues.mobile)}
                      </p>
                    </div>

                    {/* Birth date */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Birth Date <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(initialValues.birth_date),
                          )}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="birth_date"
                            type="date"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          />
                          <ErrorMessage
                            name="birth_date"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    {/* Height */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Height (cm)
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.height)}
                        </p>
                      ) : (
                        <Field
                          name="height"
                          type="number"
                          disabled={isOnboarded}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter your height"
                        />
                      )}
                    </div>

                    {/* Weight */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Weight (kg)
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.weight)}
                        </p>
                      ) : (
                        <Field
                          name="weight"
                          type="number"
                          disabled={isOnboarded}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter your weight"
                        />
                      )}
                    </div>

                    {/* Blood Group */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Blood Group
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.blood_group)}
                        </p>
                      ) : (
                        <Field
                          as="select"
                          name="blood_group"
                          disabled={isOnboarded}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        >
                          {BLOOD_GROUP_OPTIONS.map((bg) => (
                            <option key={bg.value} value={bg.value}>
                              {bg.label}
                            </option>
                          ))}
                        </Field>
                      )}
                    </div>

                    {/* Caste / Subcaste */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Caste / Subcaste
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.caste_subcaste)}
                        </p>
                      ) : (
                        <Field
                          name="caste_subcaste"
                          disabled={isOnboarded}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter caste or sub-caste"
                        />
                      )}
                    </div>

                    {/* Qualification */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Qualification
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.qualification)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="qualification"
                            type="text"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter your qualification"
                          />
                        </>
                      )}
                    </div>

                    {/* Experience */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Experience
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {initialValues.total_experience}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="total_experience"
                            type="text"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter your experience"
                          />
                        </>
                      )}
                    </div>

                    {/* is fresher */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Is Fresher ?
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            initialValues.is_fresher === true
                              ? "Yes"
                              : initialValues.is_fresher === false
                                ? "No"
                                : null,
                          )}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="is_fresher"
                            as="select"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          >
                            <option value="">Select</option>
                            {YES_NO_OPTIONS.map((item, indx) => (
                              <option key={indx} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </Field>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Education Repeater (editable on profile only) */}
                <div>
                  <EducationRepeater
                    isDisabled={isOnboarded && !isUserEditable}
                    isOnboarded={isOnboarded}
                    isUserEditable={isUserEditable}
                    values={values}
                    isViewMode={isViewMode || !isProfilePage}
                    setFieldValue={setFieldValue}
                  />
                </div>

                {/* Emergency Contact */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Emergency Contact{" "}
                    <span className="text-red-500 top-0">*</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-700">
                        Emergency Contact Number
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.emergency_contact_number)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="emergency_contact_number"
                            disabled={isOnboarded && !isUserEditable}
                            maxLength={10}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter contact number"
                          />
                          <ErrorMessage
                            name="emergency_contact_number"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Relationship to Contact
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.relationship_contact)}
                        </p>
                      ) : (
                        <Field
                          name="relationship_contact"
                          disabled={isOnboarded && !isUserEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter relationship"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-6">
                {/* Organizational Details */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Organizational Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-700">
                        Role <span className="text-red-500 top-0">*</span>
                      </label>
                      {/* Role editable only on user details page (HR/Admin) */}
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {formatRole(initialValues.role)}
                        </p>
                      ) : (
                        <>
                          <Field
                            as="select"
                            name="role"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Select Role"
                          >
                            <option value="">Select Role</option>
                            {ROLE_FIELDS.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="role"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">Company</label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            COMPANY_FIELDS.find((c) => c.value === initialValues.company)
                              ?.label,
                          )}
                        </p>
                      ) : (
                        <Field
                          as="select"
                          name="company"
                          disabled={isOnboarded && !isHRAdminEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        >
                          <option value="">Select Company</option>
                          {COMPANY_FIELDS.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </Field>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Department
                      </label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.department)}
                        </p>
                      ) : (
                        <Field
                          name="department"
                          disabled={isOnboarded && !isHRAdminEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter department"
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Designation{" "}
                        <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.designation)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="designation"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter designation"
                          />
                          <ErrorMessage
                            name="designation"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">Employee Code</label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.employee_code)}
                        </p>
                      ) : (
                        <Field
                          name="employee_code"
                          disabled={isOnboarded && !isHRAdminEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter employee code"
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">Work Location</label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.work_location)}
                        </p>
                      ) : (
                        <Field
                          name="work_location"
                          disabled={isOnboarded && !isHRAdminEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter work location"
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">Shift</label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.shift)}
                        </p>
                      ) : (
                        <Field
                          name="shift"
                          disabled={isOnboarded && !isHRAdminEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter shift"
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">Manager</label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {initialValues.manager_name}
                        </p>
                      ) : (
                        <Field
                          as="select"
                          name="manager"
                          disabled={isOnboarded && !isHRAdminEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        >
                          <option value="">Select Manager</option>
                          {allUsers
                            .filter((u) => u.id !== Number(userId))
                            .map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.first_name} {u.last_name}
                              </option>
                            ))}
                        </Field>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Date of Joining{" "}
                        <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(initialValues.joining_date),
                          )}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="joining_date"
                            type="date"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          />
                          <ErrorMessage
                            name="joining_date"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Approval Status{" "}
                        <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {initialValues.is_approved === "true"
                            ? "Approved"
                            : "Not Approved"}
                        </p>
                      ) : (
                        <>
                          <Field
                            as="select"
                            name="is_approved"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Select approval status"
                          >
                            {APPROVAL_FIELDS.map((a) => (
                              <option key={a.value} value={a.value}>
                                {a.label}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="is_approved"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700 block mb-1">
                        Employment Status
                      </label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1">
                          <span
                            className={`px-2 py-1 text-xs rounded-full border ${
                              initialValues.employment_status === "ONBOARDED"
                                ? "bg-green-50 text-green-700 border-green-300"
                                : initialValues.employment_status === "OFFBOARDED"
                                ? "bg-red-50 text-red-700 border-red-300"
                                : "bg-yellow-50 text-yellow-700 border-yellow-300"
                            }`}
                          >
                            {EMPLOYMENT_STATUS_FIELDS.find(
                              (f) => f.value === initialValues.employment_status,
                            )?.label || "Pending Onboarding"}
                          </span>
                        </p>
                      ) : (
                        <Field
                          as="select"
                          name="employment_status"
                          disabled={isOnboarded && !isHRAdminEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        >
                          {EMPLOYMENT_STATUS_FIELDS.map((f) => (
                            <option key={f.value} value={f.value}>
                              {f.label}
                            </option>
                          ))}
                        </Field>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Date of Exit
                      </label>
                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(initialValues.date_of_exit),
                          )}
                        </p>
                      ) : (
                        <Field
                          name="date_of_exit"
                          type="date"
                          disabled={isOnboarded && !isHRAdminEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700 block mb-1">
                        Attendance Permission
                      </label>

                      {isViewMode || isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {values.mark_attendance_without_site
                            ? "Allowed to mark attendance without site"
                            : "Not Allowed to mark attendance without site"}
                        </p>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="checkbox"
                            id="mark_attendance_without_site"
                            name="mark_attendance_without_site"
                            checked={
                              values.mark_attendance_without_site || false
                            }
                            onChange={(e) =>
                              setFieldValue(
                                "mark_attendance_without_site",
                                e.target.checked,
                              )
                            }
                            disabled={isOnboarded && !isHRAdminEditable}
                            className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary disabled:cursor-not-allowed disabled:bg-gray-50"
                          />
                          <label
                            htmlFor="mark_attendance_without_site"
                            className="text-sm text-gray-700"
                          >
                            Can mark attendance without site
                          </label>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700 block mb-1">
                        Default Site
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.default_site_name)}
                        </p>
                      ) : (
                        <>
                          <Field
                            as="select"
                            name="default_site"
                            disabled={assignedSites.length === 0}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          >
                            <option value="">Select a default site</option>
                            {assignedSites.map((site) => (
                              <option key={site.id} value={site.id}>
                                {site.name}
                              </option>
                            ))}
                          </Field>
                          {assignedSites.length === 0 ? (
                            <p className="text-xs text-gray-500 mt-1">
                              No sites assigned to you yet. Contact HR to get
                              assigned to a site.
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 mt-1">
                              This site is used automatically when you check
                              in.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Details */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Address Details{" "}
                    <span className="text-red-500 top-0">*</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-700">
                        Current Address
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm break-words">
                          {displayValue(initialValues.current_address)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="current_address"
                            as="textarea"
                            rows={3}
                            disabled={isOnboarded && !isUserEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter current address"
                          />
                          <ErrorMessage
                            name="current_address"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Permanent Address
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm break-words">
                          {displayValue(initialValues.permanent_address)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="permanent_address"
                            as="textarea"
                            rows={3}
                            disabled={isOnboarded && !isUserEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter permanent address"
                          />
                          <ErrorMessage
                            name="permanent_address"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Bank Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-700">
                        UAN Number <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.uan_number)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="uan_number"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter UAN number"
                          />
                          <ErrorMessage
                            name="uan_number"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Previous Employer PF No.
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.prev_emp_pf_number)}
                        </p>
                      ) : (
                        <Field
                          name="prev_emp_pf_number"
                          disabled={isOnboarded}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter PF number"
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Salary Account Number{" "}
                        <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.salary_account_number)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="salary_account_number"
                            disabled={isOnboarded && !isUserEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter salary account number"
                          />
                          <ErrorMessage
                            name="salary_account_number"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Bank IFSC Code{" "}
                        <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.bank_ifsc_code)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="bank_ifsc_code"
                            disabled={isOnboarded && !isUserEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter IFSC code"
                          />
                          <ErrorMessage
                            name="bank_ifsc_code"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Bank Name <span className="text-red-500 top-0">*</span>
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.bank_name)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="bank_name"
                            disabled={isOnboarded && !isUserEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter salary account number"
                          />
                          <ErrorMessage
                            name="bank_name"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">PAN Number</label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.pan_number)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="pan_number"
                            disabled={isOnboarded && !isUserEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter PAN number"
                            style={{ textTransform: "uppercase" }}
                          />
                          <ErrorMessage
                            name="pan_number"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">Aadhar Number</label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.aadhar_number)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="aadhar_number"
                            maxLength={12}
                            disabled={isOnboarded && !isUserEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter Aadhar number"
                          />
                          <ErrorMessage
                            name="aadhar_number"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">ESIC A/c No.</label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.esic_account_number)}
                        </p>
                      ) : (
                        <Field
                          name="esic_account_number"
                          disabled={isOnboarded && !isUserEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          placeholder="Enter ESIC account number"
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Account Opened On
                      </label>
                      {isViewMode || !isProfilePage ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(initialValues.acc_opened_on),
                          )}
                        </p>
                      ) : (
                        <Field
                          name="acc_opened_on"
                          type="date"
                          disabled={isOnboarded && !isUserEditable}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons row */}
            <div className="flex justify-end gap-4">
              {!isViewMode && (
                <button
                  type="submit"
                  disabled={
                    loading ||
                    (isOnboarded && !isUserEditable && !isHRAdminEditable)
                  }
                  className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-80"
                >
                  {loading ? "Saving..." : "Save Details"}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UserInfoTab;
